import {
  Alert,
  AlertDescription,
  AlertIcon,
  Flex,
  Select,
  Text,
} from "@chakra-ui/react";
import { ChangeEvent, FunctionComponent } from "react";
import { SegmentedControl } from "src/components/SegmentedControl";
import { MessageTemplateSpecificity } from "src/constants";
import { useGlossary } from "src/hooks/useGlossary";
import * as GQL from "src/types/graphql";

type SchoolSpecificSelectorProps = {
  isSchoolSpecificMessagesEnabled: boolean;
  messageTemplateSpecificity: MessageTemplateSpecificity;
  selectedSchoolId: string | null;
  schoolList: GQL.GetSchoolsForEnrollmentPeriod_school[];
  setMessageTemplateSpecificity: (val: MessageTemplateSpecificity) => void;
  setSelectedSchoolId: (val: string | null) => void;
};

export const SchoolSpecificSelector: FunctionComponent<
  SchoolSpecificSelectorProps
> = (props) => {
  const {
    isSchoolSpecificMessagesEnabled,
    messageTemplateSpecificity,
    selectedSchoolId,
    schoolList,
    setMessageTemplateSpecificity,
    setSelectedSchoolId,
  } = props;

  const { glossary } = useGlossary();

  const handleChangeSchool = async (event: ChangeEvent<HTMLSelectElement>) => {
    if (event.target.value === "") {
      setSelectedSchoolId(null);
    } else {
      setSelectedSchoolId(event.target.value);
    }
  };

  return (
    <>
      <SegmentedControl
        options={[
          {
            label: "Standard",
            value: MessageTemplateSpecificity.Standard,
          },
          {
            label: "School specific",
            value: MessageTemplateSpecificity.SchoolSpecific,
            disabled: !isSchoolSpecificMessagesEnabled,
            disabledReason:
              "To configure school specific messages, first turn on School specific automatic messages in Settings",
          },
        ]}
        onChange={setMessageTemplateSpecificity}
        value={messageTemplateSpecificity}
      />
      {messageTemplateSpecificity ===
        MessageTemplateSpecificity.SchoolSpecific && (
        <>
          <Alert status="info" borderRadius={6}>
            <AlertIcon />
            <AlertDescription fontSize="sm">
              You are viewing school specific content. Select from the dropdown
              which school content you want to view.
            </AlertDescription>
          </Alert>
          <Flex direction="column" gap={2}>
            <Text fontSize="sm" fontWeight="bold" color="gray.700">
              Select a school to see their messages
            </Text>
            <Select
              onChange={handleChangeSchool}
              value={selectedSchoolId ?? ""}
              placeholder={glossary`Select school`}
            >
              {schoolList.map((school) => (
                <option value={school.id} key={school.id}>
                  {school.name}
                </option>
              ))}
            </Select>
            {selectedSchoolId && (
              <Text fontSize="sm" color="gray.600">
                The content is specific to this school only.
              </Text>
            )}
          </Flex>
        </>
      )}
    </>
  );
};
